import { apiClient } from 'api';
import { API_ROUTES } from 'constants/routes';
import { GoogleLoginResponse, GoogleRedirectResponse } from 'modules/auth/types';
import { IRole } from 'modules/users/types';

/** Response shape from GET /auth/me (cookie or Bearer auth). */
export interface AuthMeResponse {
    id: string;
    email: string;
    first_name: string;
    last_name: string;
    location_ids: number[];
    franchise_partner_id: string | null;
    roles: IRole[];
}

/**
 * Function that makes an asynchronous request to Google login endpoint.
 *
 * @returns {Promise<GoogleRedirectResponse>} A Promise that resolves with the response data from the Google login request.
 */
export const googleLoginRequest = async (): Promise<GoogleRedirectResponse> => {
    const response = await apiClient.get<GoogleRedirectResponse>(API_ROUTES.auth.googleLogin);
    return response.data;
};

/**
 * Function to exchange Google OAuth code for access token.
 * When using httpOnly cookies, the backend sets cookies; the returned body is still present for legacy clients.
 *
 * @param {Object} options - Options object.
 * @param {string} options.code - The OAuth code to exchange for access token.
 *
 * @returns {Promise<GoogleLoginResponse>} The response containing the access token and related data.
 */
export const googleCodeExchangeRequest = async ({ code }: { code: string }): Promise<GoogleLoginResponse> => {
    const response = await apiClient.get<GoogleLoginResponse>(API_ROUTES.auth.googleCodeExchange, { params: { code } });
    return response.data;
};

/**
 * Fetches the current user from the backend (cookie or Bearer token).
 * Used to bootstrap session when using httpOnly cookies.
 */
export const authMeRequest = async (): Promise<AuthMeResponse> => {
    const response = await apiClient.get<AuthMeResponse>(API_ROUTES.auth.me);
    return response.data;
};

/**
 * Clears auth httpOnly cookies on the backend. Call with credentials.
 */
export const authLogoutRequest = async (): Promise<void> => {
    await apiClient.post(API_ROUTES.auth.logout);
};
